import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_PROJECTS_LOAD,
    ACTION_PROJECTS_LOAD_BY_ID,
    ACTION_PROJECTS_LOAD_FAILURE,
    ACTION_PROJECTS_LOAD_SUCCESS,

    ACTION_PROJECTS_SAVE,
    ACTION_PROJECTS_SAVE_SUCCESS,
    ACTION_PROJECTS_SAVE_FAILURE,

    ACTION_PROJECTS_CREATE,
    ACTION_PROJECTS_CREATE_SUCCESS,
    ACTION_PROJECTS_CREATE_FAILURE,

    ACTION_PROJECTS_VALIDATE,
    ACTION_PROJECTS_VALIDATE_SUCCESS,
    ACTION_PROJECTS_VALIDATE_FAILURE,

    ACTION_PROJECTS_CLEAR,
    ACTION_PROJECTS_FILTER,

    ProjectAction
} from '../actions/project';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IProjectState {
    formData: Dtos.Project | Dtos.Project[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.Project[];
    formLength: number;
}

const initialState: IProjectState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IProjectState, _isLoading: boolean): IProjectState {
    const newState: IProjectState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IProjectState,
    formData: Dtos.Project | Dtos.Project[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IProjectState {
    const newState: IProjectState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: IProjectState, formFilter: IFormFilter): IProjectState {

    const projects: Dtos.Project[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = projects ? projects.length : 0;

    const formFilteredData = projects ? projects.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.display.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IProjectState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function loadFailure(state: IProjectState, responseStatus: Dtos.ResponseStatus): IProjectState {
    const newState: IProjectState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IProjectState, propertiesToClear?: string[]): IProjectState {

    if (!propertiesToClear) {
        const newState: IProjectState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IProjectState, _isSaving: boolean): IProjectState {
    const newState: IProjectState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IProjectState,
    formData: Dtos.Project | Dtos.Project[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IProjectState {
    const newState: IProjectState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IProjectState, responseStatus: Dtos.ResponseStatus): IProjectState {
    const newState: IProjectState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IProjectState, _isCreating: boolean): IProjectState {
    const newState: IProjectState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IProjectState,
    formData: Dtos.Project,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IProjectState {
    const newState: IProjectState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IProjectState, responseStatus: Dtos.ResponseStatus): IProjectState {
    const newState: IProjectState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IProjectState, _isValidating: boolean): IProjectState {
    const newState: IProjectState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IProjectState,
    formData: Dtos.Project,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IProjectState {
    const newState: IProjectState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IProjectState, responseStatus: Dtos.ResponseStatus): IProjectState {
    const newState: IProjectState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const ProjectReducer: Reducer<IProjectState> = (state: IProjectState = initialState, action: ProjectAction) => {
    switch (action.type) {
        case ACTION_PROJECTS_LOAD:
        case ACTION_PROJECTS_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_PROJECTS_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_PROJECTS_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_PROJECTS_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_PROJECTS_SAVE:
            return saving(state, true);
        case ACTION_PROJECTS_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_PROJECTS_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_PROJECTS_CREATE:
            return creating(state, true);
        case ACTION_PROJECTS_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_PROJECTS_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_PROJECTS_VALIDATE:
            return validating(state, true);
        case ACTION_PROJECTS_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_PROJECTS_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_PROJECTS_FILTER:
            return filter(state, action.formFilter);
    }

    return state;
}

export default ProjectReducer;